import { useState } from "react";
import { ADVANCED_CARE_PROGRAM, ConsentedPolicyEnum } from "@/core/constants";
import type { PatientConsent, UUID } from "@/core/types";
import { cn } from "@/lib/helpers/cn";
import { getConsentPayload } from "@/lib/helpers/getConsentPayload";
import { usePolicyMetadataQuery } from "@/lib/queries/usePolicyMetadata";
import { Button2 } from "@/ui2/atoms/Button";
import { showToast } from "@/providers/ToastProvider";
import { captureErrorMessage, parseError } from "@/lib/helpers/captureErrorMessage";
import { useAppendConsentMutation } from "@/lib/mutations/useAppendConsentMutation";
import { PATIENT_CONSENTS_QUERY_KEYS } from "@/lib/queries/usePatientConsents";
import { useQueryClient } from "@tanstack/react-query";
import { DEFAULT_TOAST_TIMEOUT } from "@/lib/constants/delay";
import { OptOutConfirmationModal } from "./OptOutConfirmationModal";

type OptOutButtonProps = {
  patientId: UUID;
  patientName: string;
  sortKey: string;
};

const policyType = ADVANCED_CARE_PROGRAM;

export function OptOutButton({ patientId, patientName, sortKey }: OptOutButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { mutate: appendConsent } = useOptimisticOptOut({
    patientId,
    patientName,
    sortKey,
  });

  const { data: policyMetadata, isLoading: isLoadingPolicyMetadata } = usePolicyMetadataQuery();

  const handleOptOut = async () => {
    if (!policyMetadata) return;

    const consentPayload = getConsentPayload({
      consentResponse: false,
      consentType: ConsentedPolicyEnum[policyType],
      patientId,
      policyMetadata,
      policyVersion: policyMetadata[policyType].current_version,
      consentGivenBy: patientId,
    });

    appendConsent(consentPayload);
    setIsModalOpen(false);
  };

  return (
    <>
      <Button2
        onPress={() => setIsModalOpen(true)}
        className={cn(
          "tw:p-0 tw:border-transparent tw:h-auto",
          "tw:text-text-error-primary",
          "tw:hover:text-text-error-secondary",
          "tw:active:text-text-error-primary",
        )}
        variant="text"
        disabled={isLoadingPolicyMetadata}
        size="sm"
      >
        Opt Out
      </Button2>

      <OptOutConfirmationModal
        patientName={patientName}
        onOpenChange={setIsModalOpen}
        isModalOpen={isModalOpen}
        onConfirmPress={handleOptOut}
        onCancelPress={() => setIsModalOpen(false)}
      />
    </>
  );
}

function useOptimisticOptOut({ patientName, patientId, sortKey }: OptOutButtonProps) {
  const queryClient = useQueryClient();

  return useAppendConsentMutation({
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: PATIENT_CONSENTS_QUERY_KEYS.list(patientId) });

      const previousConsents = queryClient.getQueryData<PatientConsent[]>(
        PATIENT_CONSENTS_QUERY_KEYS.list(patientId),
      );

      const updatedConsent = {
        ...previousConsents.find((c) => c.sort_key === sortKey),
        consent_response: false,
      };

      queryClient.setQueryData(PATIENT_CONSENTS_QUERY_KEYS.list(patientId), [
        ...previousConsents,
        updatedConsent,
      ]);

      return { previousConsents, updatedConsent };
    },

    onError: (error, _variables, context) => {
      queryClient.setQueryData(
        PATIENT_CONSENTS_QUERY_KEYS.list(patientId),
        context.previousConsents,
      );

      showToast([
        {
          heading: "Something went wrong. Please reload the page and try again.",
          variant: "error",
        },
        { timeout: DEFAULT_TOAST_TIMEOUT },
      ]);

      captureErrorMessage(parseError(error), {
        tags: {
          component: "OptOutButton",
          patientId,
          consentType: ADVANCED_CARE_PROGRAM,
          errorType: "mutation",
        },
      });
    },

    onSuccess: () => {
      showToast([
        {
          heading: `${patientName} successfully opted out of the Advanced Care Program.`,
          variant: "success",
        },
        { timeout: DEFAULT_TOAST_TIMEOUT },
      ]);
    },
    /** NOTE: We don't want to invalidate cache onSettled function - by design BE needs a few seconds to update the consents table.
     * If we do invalidate the cache immediately, the user will see the old consent state.
     */
  });
}
